import React, { useRef, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const DownloadPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get data from location state
  const { 
    htmlContent, 
    documentUrl,
    documentPath,
    documentTitle = 'Legal Document', 
    documentType = '',
    documentId = null,
    fromDashboard = false
  } = location.state || {};
  
  // Handle PDF URL setup
  useEffect(() => {
    const setupPdfUrl = async () => {
      try {
        setIsLoading(true);
        
        // Case 1: We have a documentId - use the edge function like in DocumentViewPage
        if (documentId) {
          console.log("Using edge function to get signed URL for document ID:", documentId);
          const { data: signedUrlData, error: signedUrlError } = await supabase.functions.invoke("get-signed-url", {
            method: "POST",
            body: { documentId: documentId, forDownload: false },
          });
          
          if (signedUrlError) {
            console.error("Error invoking edge function for PDF view:", signedUrlError);
            setError("Failed to load document for preview. Please try again.");
          } else if (signedUrlData?.signedUrl) {
            console.log("Successfully retrieved signed URL for PDF view");
            setPdfUrl(signedUrlData.signedUrl);
          }
          setIsLoading(false);
          return;
        }
        
        // Case 2: We already have a direct documentUrl from the edge function
        if (documentUrl) {
          console.log("Using direct document URL:", documentUrl);
          setPdfUrl(documentUrl);
          setIsLoading(false);
          return;
        }
        
        // Case 3: We have a documentPath and need to create a signed URL
        if (documentPath) {
          console.log("Creating signed URL from path:", documentPath);
          const { data, error } = await supabase
            .storage
            .from('documents')
            .createSignedUrl(documentPath, 3600); // 1 hour expiry
          
          if (error) {
            console.error('Error creating signed URL for PDF:', error);
            setError('Could not generate PDF preview link');
          } else if (data?.signedUrl) {
            console.log("Successfully created signed URL");
            setPdfUrl(data.signedUrl);
          }
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error("Error in setupPdfUrl:", err);
        setError("Failed to prepare document for viewing");
        setIsLoading(false);
      }
    };

    setupPdfUrl();
  }, [documentId, documentUrl, documentPath]);

  const handleBack = () => {
    if (fromDashboard) {
      navigate('/dashboard');
    } else {
      navigate('/templates');
    }
  };

  const handleNewDocument = () => {
    navigate('/');
  };

  const handleDownloadDocument = async (docId, docTitle) => {
    try {
      setDownloadLoading(true);
      setError(null);

      // Call the edge function to get the signed URL
      const { data, error } = await supabase.functions.invoke("get-signed-url", {
        method: "POST",
        body: { documentId: docId, forDownload: true },
      });

      if (error) {
        console.error("Error invoking edge function:", error);
        setError("Failed to fetch document link. Please try again.");
        return;
      }

      if (!data?.signedUrl) {
        setError("Could not generate document link.");
        return;
      }

      // Trigger PDF download instead of opening in a new tab
      console.log("Downloading document from URL:", data.signedUrl);
      const a = window.document.createElement('a');
      a.href = data.signedUrl;
      a.download = `${docTitle || 'document'}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      
    } catch (error) {
      console.error("Error accessing document:", error);
      setError("Failed to download document. Please try again later.");
    } finally {
      setDownloadLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-lg text-gray-600">Loading document...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
        <button 
          onClick={handleBack}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10">
      <div className="mb-8 flex justify-between items-center">
        <h1 className="text-3xl font-bold">{documentTitle || `${documentType} Document`}</h1>
        <div className="flex space-x-4">
          <button 
            onClick={handleBack}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back
          </button>
          <button 
            onClick={() => handleDownloadDocument(documentId, documentTitle)}
            disabled={downloadLoading}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center disabled:opacity-70"
          >
            {downloadLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Preparing Download...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download as PDF
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto bg-white rounded-lg overflow-hidden shadow-lg p-8 border">
        {pdfUrl ? (
          // Display PDF using iframe for better compatibility
          <iframe 
            src={pdfUrl} 
            className="w-full h-screen"
            title="Document Preview"
          />
        ) : (
          // Display HTML content
          <div 
            ref={contentRef}
            className="document-content"
            dangerouslySetInnerHTML={htmlContent ? { __html: htmlContent } : undefined}
          />
        )}
      </div>

      <div className="mt-10 flex justify-center">
        <button 
          onClick={handleNewDocument}
          className="px-8 py-3 bg-gray-100 text-gray-700 rounded-md text-lg font-medium inline-flex items-center hover:bg-gray-200 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
          </svg>
          Create New Document
        </button>
      </div>
    </div>
  );
};

export default DownloadPage;